import { useMemo } from "react"
import { CapTableFunding } from "@/types"

export interface FundraisingMarker {
  x: string
  label: string
}

/**
 * Snaps fundraising dates to the nearest date string present in the chart data.
 * This ensures ReferenceLine x values align with actual data points (required for
 * string-based XAxis in Recharts).
 */
export function snapFundraisingDates(
  capTableFunding: CapTableFunding[],
  chartDates: string[]
): FundraisingMarker[] {
  if (!chartDates.length || !capTableFunding.length) return []

  const timestamps = chartDates.map((d) => ({ str: d, ts: new Date(d).getTime() }))

  const markers = capTableFunding
    .filter((f) => f.issueDate && f.label)
    .map((f) => {
      const target = new Date(f.issueDate!).getTime()
      let closest = timestamps[0]
      for (const t of timestamps) {
        if (Math.abs(t.ts - target) < Math.abs(closest.ts - target)) closest = t
      }
      return { x: closest.str, label: f.label! }
    })
    // Deduplicate: if multiple rounds snap to the same date, keep the first
    .filter((v, i, arr) => arr.findIndex((a) => a.x === v.x) === i)

  return markers
}

/** Hook wrapper for convenience */
export function useFundraisingMarkers(
  capTableFunding: CapTableFunding[],
  chartDates: string[]
): FundraisingMarker[] {
  return useMemo(
    () => snapFundraisingDates(capTableFunding, chartDates),
    [capTableFunding, chartDates]
  )
}
